(function () {
  if (window.customElements.get('pi-bundle-products')) return;

  class PiBundleProducts extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this._cartError = false;

      const configNode = this.querySelector('[data-pi-bp-config]');
      this.config = configNode ? JSON.parse(configNode.textContent) : {};
      this.formId = this.dataset.formId;
      this.sectionId = this.config.sectionId || this.dataset.sectionId;
      this.propertyLabel = this.config.propertyLabel || this.dataset.propertyLabel || 'Includes';
      this.addNestedProducts =
        typeof this.config.addNestedProducts === 'boolean'
          ? this.config.addNestedProducts
          : this.dataset.addNestedProducts === 'true';
      this.moneyFormat = this.config.moneyFormat;
      this.soldOutText = this.config.soldOutText || window.variantStrings?.soldOut || 'Sold out';

      this.itemElements = [...this.querySelectorAll('[data-pi-bp-item]')];
      this.variantSelect = this.querySelector('[data-pi-bp-variant-select]');
      this.selectableItemElements = this.itemElements.filter(
        (element) => element.dataset.isSelectable === 'true'
      );

      this.onVariantChange = this.onVariantChange.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onMainVariantChange = this.onMainVariantChange.bind(this);

      this.variantSelect?.addEventListener('change', this.onVariantChange);

      const form = this.getProductForm();
      if (form) {
        form.addEventListener('submit', this.onFormSubmit, true);
      }

      if (typeof subscribe === 'function') {
        this.variantChangeUnsub = subscribe(PUB_SUB_EVENTS.variantChange, this.onMainVariantChange);
      }

      this.updateFixedItemStates();

      if (this.variantSelect) {
        this.onVariantChange();
      } else {
        this.updateSubmitButton(null);
        this.updatePropertyInput();
      }

      const productInfo = this.closest('product-info');
      const syncSubmitState = () => this.updateSubmitButton(null);
      productInfo?.addEventListener('product-info:loaded', syncSubmitState, { once: true });
      window.setTimeout(syncSubmitState, 0);
    }

    disconnectedCallback() {
      this.variantSelect?.removeEventListener('change', this.onVariantChange);
      this.getProductForm()?.removeEventListener('submit', this.onFormSubmit, true);
      this.variantChangeUnsub?.();
      this._initialized = false;
    }

    getProductForm() {
      if (!this.formId) return null;
      return document.getElementById(this.formId);
    }

    getProductFormElement() {
      return this.getProductForm()?.closest('product-form') || null;
    }

    getCartElement() {
      return (
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer') ||
        document.querySelector('pi-cart-drawer')
      );
    }

    getPropertyInput() {
      const form = this.getProductForm();
      if (!form) return null;

      let input = form.querySelector('[data-pi-bp-property]');

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.setAttribute('data-pi-bp-property', '');
        form.appendChild(input);
      }

      input.name = `properties[${this.propertyLabel}]`;
      return input;
    }

    onMainVariantChange({ data }) {
      if (!data || String(data.sectionId) !== String(this.sectionId)) return;
      this.updateSubmitButton(data.variant?.available ?? false);
    }

    onFormSubmit(evt) {
      if (!this.itemElements.length) return;

      if (!this.isBundleAvailable()) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        this.updateSubmitButton(false);
        return;
      }

      evt.preventDefault();
      evt.stopImmediatePropagation();

      this.syncSelectableLabels();
      this.updatePropertyInput();
      this.addBundleToCart(evt);
    }

    collectFormProperties(form) {
      const properties = {};

      form.querySelectorAll('input[name^="properties["], textarea[name^="properties["]').forEach((input) => {
        if (!input.name || input.disabled) return;
        const match = input.name.match(/^properties\[(.+)\]$/);
        if (!match || input.value === '') return;
        properties[match[1]] = input.value;
      });

      return properties;
    }

    buildCartItems() {
      const form = this.getProductForm();
      if (!form) return null;

      const mainVariantId = parseInt(form.querySelector('[name="id"]')?.value, 10);
      if (!mainVariantId) return null;

      const productInfo = this.closest('product-info');
      const quantityInput =
        form.querySelector('[name="quantity"]') || productInfo?.querySelector('.quantity__input');
      const mainQuantity = parseInt(quantityInput?.value, 10) || 1;

      const properties = this.collectFormProperties(form);
      const parentLine = {
        id: mainVariantId,
        quantity: mainQuantity,
      };

      if (Object.keys(properties).length) {
        parentLine.properties = properties;
      }

      const sellingPlanInput = form.querySelector('[name="selling_plan"]');
      if (sellingPlanInput?.value) {
        parentLine.selling_plan = sellingPlanInput.value;
      }

      const items = [parentLine];

      if (this.addNestedProducts) {
        this.itemElements.forEach((element) => {
          const isSelectable = element.dataset.isSelectable === 'true';
          const variantId = isSelectable
            ? parseInt(this.getSelectedSelectableVariantId(), 10)
            : parseInt(element.dataset.variantId, 10);
          if (!variantId) return;

          items.push({
            id: variantId,
            quantity: parseInt(element.dataset.count, 10) || 1,
            parent_id: mainVariantId,
          });
        });
      }

      return items;
    }

    async addBundleToCart(evt) {
      const items = this.buildCartItems();
      if (!items?.length) return;

      const productFormEl = this.getProductFormElement();
      const submitButton = productFormEl?.querySelector('[type="submit"]');
      const submitButtonText = submitButton?.querySelector('span');
      const spinner = productFormEl?.querySelector('.loading__spinner');
      const cart = this.getCartElement();

      this._cartError = false;
      submitButton?.setAttribute('aria-disabled', 'true');
      submitButton?.classList.add('loading');
      spinner?.classList.remove('hidden');

      const sections = cart?.getSectionsToRender?.().map((section) => section.id) || [
        'pi-cart-drawer',
        'pi-cart-icon-bubble',
      ];

      const payload = {
        items,
        sections,
        sections_url: window.location.pathname,
      };

      cart?.setActiveElement?.(document.activeElement);

      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });

        const parsedState = await response.json();

        if (parsedState.status) {
          this._cartError = true;

          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'pi-bundle-products',
              productVariantId: items[0].id,
              errors: parsedState.errors || parsedState.description,
              message: parsedState.message,
            });
          }

          productFormEl?.handleErrorMessage?.(parsedState.description);
          return;
        }

        productFormEl?.handleErrorMessage?.(false);

        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }

        if (typeof publish === 'function') {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'pi-bundle-products',
            productVariantId: items[0].id,
            cartData: parsedState,
          });
        }

        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              window.setTimeout(() => {
                cart.renderContents(parsedState);
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          cart.renderContents(parsedState);
        }
      } catch (error) {
        this._cartError = true;
        console.error('pi-bundle-products: add to cart failed', error);
        productFormEl?.handleErrorMessage?.(error.message);
      } finally {
        submitButton?.classList.remove('loading');
        spinner?.classList.add('hidden');

        if (cart?.classList?.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }

        if (!this._cartError) {
          submitButton?.removeAttribute('aria-disabled');
        }
      }
    }

    onVariantChange() {
      if (!this.variantSelect) return;

      const selectedOption = this.variantSelect.selectedOptions[0];
      if (!selectedOption?.value) {
        this.updateSelectableAvailability(false);
        this.updatePropertyInput();
        this.updateSubmitButton(null);
        return;
      }

      const variantTitle = selectedOption.dataset.variantTitle || selectedOption.textContent.trim();
      const variantPrice = parseInt(selectedOption.dataset.variantPrice, 10);
      const variantUrl = selectedOption.dataset.variantUrl;
      const variantAvailable = selectedOption.dataset.variantAvailable === 'true';

      this.selectableItemElements.forEach((element) => {
        const variantLabel = element.querySelector('[data-pi-bp-variant-label]');
        if (variantLabel) {
          variantLabel.textContent = variantTitle === 'Default Title' ? '' : variantTitle;
        }

        const priceElement = element.querySelector('[data-pi-bp-price]');
        if (priceElement && !Number.isNaN(variantPrice)) {
          priceElement.textContent = this.formatPrice(variantPrice);
        }

        if (variantUrl) {
          element.querySelectorAll('[data-pi-bp-item-link]').forEach((link) => {
            link.href = variantUrl;
          });
        }

        const variantImage = selectedOption.dataset.variantImage;
        const imageElement = element.querySelector('[data-pi-bp-item-image]');
        if (imageElement && variantImage) {
          imageElement.src = variantImage;
          imageElement.removeAttribute('srcset');
        }

        element.dataset.variantId = selectedOption.value;
        this.setSelectableItemLabel(element, variantTitle);
      });

      this.updateSelectableAvailability(variantAvailable);
      this.updatePropertyInput();
      this.updateSubmitButton(null);
    }

    updateFixedItemStates() {
      this.itemElements
        .filter((element) => element.dataset.isSelectable !== 'true')
        .forEach((element) => {
          const inStock = element.dataset.variantInStock === 'true';
          this.setItemSoldOutState(element, inStock);
        });
    }

    updateSelectableAvailability(isAvailable) {
      if (typeof isAvailable !== 'boolean') {
        const selectedOption = this.variantSelect?.selectedOptions[0];
        isAvailable = selectedOption?.dataset.variantAvailable === 'true';
      }

      this.selectableItemElements.forEach((element) => {
        element.dataset.variantInStock = isAvailable ? 'true' : 'false';
        this.setItemSoldOutState(element, isAvailable);
      });
    }

    setItemSoldOutState(element, inStock) {
      element.classList.toggle('pi-bp__item--sold-out', !inStock);
      element.dataset.variantInStock = inStock ? 'true' : 'false';

      let badge = element.querySelector('[data-pi-bp-sold-out]');
      if (!inStock) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'pi-bp__item-sold-out';
          badge.setAttribute('data-pi-bp-sold-out', '');
          badge.textContent = this.soldOutText;
          element.querySelector('.pi-bp__item-details')?.appendChild(badge);
        }
      } else if (badge) {
        badge.remove();
      }
    }

    isBundleAvailable() {
      for (const element of this.itemElements) {
        if (element.dataset.variantInStock === 'false') return false;
      }

      for (const item of this.config.fixedItems || []) {
        if (!item.available) return false;
      }

      if (this.config.hasSelectable) {
        const selectedId = this.getSelectedSelectableVariantId();
        if (!selectedId) return false;

        const selectableVariants = this.config.selectableVariants || {};
        if (selectableVariants[String(selectedId)] === false) return false;
      }

      return true;
    }

    getSelectedSelectableVariantId() {
      if (this.variantSelect) {
        const selectedOption = this.variantSelect.selectedOptions[0];
        return selectedOption?.value || null;
      }

      const selectableItem = this.selectableItemElements[0];
      return selectableItem?.dataset.variantId || null;
    }

    updateSubmitButton(mainVariantAvailable) {
      const productForm = this.getProductFormElement();
      if (!productForm?.toggleSubmitButton) return;

      const submitButton = productForm.querySelector('[type="submit"]');
      if (!submitButton) return;

      if (mainVariantAvailable === null) {
        mainVariantAvailable = !submitButton.hasAttribute('disabled');
      }

      const bundleAvailable = this.isBundleAvailable();
      const shouldDisable = !mainVariantAvailable || !bundleAvailable;

      productForm.toggleSubmitButton(shouldDisable, shouldDisable ? this.soldOutText : undefined);

      const paymentButton = productForm.querySelector('.shopify-payment-button');
      if (paymentButton) {
        paymentButton.classList.toggle('pi-bp__payment-hidden', shouldDisable);
        paymentButton.setAttribute('aria-hidden', shouldDisable ? 'true' : 'false');
      }
    }

    setSelectableItemLabel(element, variantTitle) {
      const productTitle = element.dataset.productTitle || '';
      const label = this.formatItemLabel(variantTitle, productTitle);
      if (label) {
        element.dataset.label = label;
      }
    }

    syncSelectableLabels() {
      if (!this.variantSelect) return;

      const selectedOption = this.variantSelect.selectedOptions[0];
      if (!selectedOption?.value) return;

      const variantTitle = selectedOption.dataset.variantTitle || selectedOption.textContent.trim();
      this.selectableItemElements.forEach((element) => {
        this.setSelectableItemLabel(element, variantTitle);
      });
    }

    formatItemLabel(variantTitle, productTitle) {
      const cleanVariant = variantTitle && variantTitle !== 'Default Title' ? variantTitle.trim() : '';
      const cleanTitle = (productTitle || '').trim();

      if (cleanVariant && cleanTitle) {
        return `${cleanVariant} ${cleanTitle}`;
      }

      return cleanTitle || cleanVariant;
    }

    getItemLabel(element) {
      const isSelectable = element.dataset.isSelectable === 'true';

      if (isSelectable) {
        const selectedId = this.getSelectedSelectableVariantId();
        if (!selectedId) return '';

        if (this.variantSelect) {
          const selectedOption = this.variantSelect.selectedOptions[0];
          const variantTitle = selectedOption.dataset.variantTitle || selectedOption.textContent.trim();
          const productTitle = element.dataset.productTitle || '';
          return this.formatItemLabel(variantTitle, productTitle);
        }
      }

      return element.dataset.label || '';
    }

    buildPropertyValue() {
      const parts = [];

      this.itemElements.forEach((element) => {
        const isSelectable = element.dataset.isSelectable === 'true';
        const variantId = isSelectable
          ? parseInt(this.getSelectedSelectableVariantId(), 10)
          : parseInt(element.dataset.variantId, 10);
        if (!variantId) return;

        const count = parseInt(element.dataset.count, 10) || 1;
        const label = this.getItemLabel(element);

        if (!label) return;

        for (let i = 0; i < count; i += 1) {
          parts.push(label);
        }
      });

      return parts.join(' + ');
    }

    updatePropertyInput() {
      const input = this.getPropertyInput();
      if (!input) return;

      input.value = this.buildPropertyValue();
    }

    formatPrice(cents) {
      if (window.PiUtils?.formatMoney) {
        return window.PiUtils.formatMoney(cents, this.moneyFormat);
      }
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, this.moneyFormat);
      }
      return (cents / 100).toFixed(2);
    }
  }

  window.customElements.define('pi-bundle-products', PiBundleProducts);
})();
